import { ProductLevel, TradeDirection, TradeFlow } from "@atlas-int/data/types";
import { computeMonetaryValueByTradeFlow, computeTotalSumByTradeFlow } from "@atlas-int/data";
import { groups } from "d3";
import { createProductLookupIndex } from "@atlas-int/data";
import type { ProductMetadatum } from "@atlas-int/data/types";
import type { TradeDataItem, EnrichedTradeDataItem } from "@atlas-int/data/types";

const productShareThreshold = 0.01 / 100; // Product share threshold of 0.01%

interface TransformProductsParams {
  productLevel: number | undefined;
  data: TradeDataItem[];
  products: ProductMetadatum[];
  totalValue: number | undefined;
  tradeDirection: TradeDirection;
  tradeFlow: TradeFlow;
}

const transformProducts = ({
  productLevel,
  data,
  products,
  totalValue,
  tradeDirection,
  tradeFlow,
}: TransformProductsParams): EnrichedTradeDataItem[] | undefined => {
  if (data.length === 0) {
    return undefined;
  }

  // Use shared utility for O(1) product lookups
  const productLookup = createProductLookupIndex(products);

  if (productLevel === ProductLevel.ProductSection) {
    const dataWithParents = data.map((d: TradeDataItem) => {
      const productId = d.productId;
      let findMatchingMetadata = productLookup.get(productId) as ProductMetadatum | undefined;
      let nameEn = undefined;
      let productCode = undefined;
      let sectorId = undefined;
      if (findMatchingMetadata) {
        nameEn = findMatchingMetadata.nameShortEn;
        productCode = findMatchingMetadata.code;
        sectorId = findMatchingMetadata.topParent?.productId;
      }

      return {
        ...d,
        topLevelParent: "root",
        nameEn,
        productCode,
        sectorId,
      };
    });

    const rootObject = { productId: "root", topLevelParent: undefined } as EnrichedTradeDataItem;
    return [rootObject, ...dataWithParents];
  } else {
    let topLevelParents: Set<string> = new Set();

    data.forEach((d: TradeDataItem) => {
      const productId = d.productId;
      let findMatchingMetadata = productLookup.get(productId) as ProductMetadatum | undefined;
      let topLevelParent;
      if (findMatchingMetadata) {
        topLevelParent = findMatchingMetadata.topParent?.productId;

        topLevelParents.add(topLevelParent);
      }
    });

    const dataWithParents = data.map((d: TradeDataItem) => {
      const productId = d.productId;
      let findMatchingMetadata = productLookup.get(productId) as ProductMetadatum | undefined;
      let topLevelParent = undefined;
      let nameEn = undefined;
      let productCode = undefined;
      let sectorId;
      if (findMatchingMetadata) {
        topLevelParent = findMatchingMetadata.topParent?.productId;
        nameEn = findMatchingMetadata.nameShortEn;
        productCode = findMatchingMetadata.code;
        sectorId = findMatchingMetadata.topParent?.productId;
      }

      return {
        ...d,
        topLevelParent,
        nameEn,
        productCode,
        sectorId,
      };
    });

    let topLevelParentsObjects = [...topLevelParents.values()].map((t) => {
      // Find metadata for the top-level parent (section)
      let findMatchingMetadata = productLookup.get(t) as ProductMetadatum | undefined;

      return {
        productId: t,
        topLevelParent: "root",
        nameEn: findMatchingMetadata?.nameShortEn,
        productCode: findMatchingMetadata?.code,
      } as EnrichedTradeDataItem;
    });

    const rootObject = { productId: "root", topLevelParent: undefined } as EnrichedTradeDataItem;

    if (totalValue && productLevel === ProductLevel.Product6digit) {
      let productsAboveThreshold = dataWithParents.filter((product) => {
        let monetaryValue = computeMonetaryValueByTradeFlow({
          datum: product,
          tradeFlow,
          tradeDirection,
        });
        if (monetaryValue) {
          return monetaryValue / totalValue >= productShareThreshold;
        } else {
          return false;
        }
      });

      let productsBelowThreshold = dataWithParents.filter((product) => {
        let monetaryValue = computeMonetaryValueByTradeFlow({
          datum: product,
          tradeFlow,
          tradeDirection,
        });
        if (monetaryValue) {
          return monetaryValue / totalValue < productShareThreshold;
        } else {
          return false;
        }
      });

      let productsBelowThresholdGrouped = groups(
        productsBelowThreshold,
        (item) => item.topLevelParent,
      ).map(([productId, items]) => {
        const sumExportValue = computeTotalSumByTradeFlow({
          data: items,
          tradeFlow,
          tradeDirection: TradeDirection.Exports,
        });
        const sumImportValue = computeTotalSumByTradeFlow({
          data: items,
          tradeFlow,
          tradeDirection: TradeDirection.Imports,
        });

        let matchingSectionMetadata = productLookup.get(productId as string) as ProductMetadatum | undefined;

        let groupNameEn = matchingSectionMetadata && matchingSectionMetadata.productLevel === ProductLevel.ProductSection
          ? `Other ${matchingSectionMetadata.nameShortEn}, < ${productShareThreshold * 100}%`
          : `Other, < ${productShareThreshold * 100}%`;
        return {
          ...items[0],
          exportValue: sumExportValue,
          importValue: sumImportValue,
          groupedProduct: true,
          nameEn: groupNameEn,
        };
      });

      return [
        rootObject,
        ...topLevelParentsObjects,
        ...productsAboveThreshold,
        ...productsBelowThresholdGrouped,
      ];
    } else {
      return [rootObject, ...topLevelParentsObjects, ...dataWithParents];
    }
  }
};

export default transformProducts;
